import { useCallback, useEffect, useState } from 'react';
import { Sprite } from '../components/Sprite.tsx';

// Public team feed. Sort by `new` (chronological) or `confirmed` (peer-
// attested "works" count desc). One per-team card per row with sprite
// cluster + attestation badge + click-through to /community/:id.

type ListedTeam = {
  id: number;
  isOwn: boolean;
  regulationId: string | null;
  name: string;
  blurb: string | null;
  replicaCode: string | null;
  archetypeTags: string[] | null;
  createdAt: string;
  authorDisplayName: string | null;
  worksCount: number;
  brokenCount: number;
  sprites: string[];
};

type ListResponse = { teams: ListedTeam[] };

function navTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function urlState(): { sort: 'new' | 'confirmed'; q: string } {
  if (typeof window === 'undefined') return { sort: 'new', q: '' };
  const p = new URLSearchParams(window.location.search);
  const sort = p.get('sort') === 'confirmed' ? 'confirmed' : 'new';
  return { sort, q: p.get('q') ?? '' };
}

export function Community() {
  const [sort, setSort] = useState<'new' | 'confirmed'>(() => urlState().sort);
  const [q, setQ] = useState<string>(() => urlState().q);
  const [teams, setTeams] = useState<ListedTeam[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Reflect sort + q into the URL so a back-nav from /community/:id lands
  // the user on their previous filter view rather than the default.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams();
    if (sort !== 'new') p.set('sort', sort);
    if (q.trim()) p.set('q', q.trim());
    const qs = p.toString();
    const target = `/community${qs ? `?${qs}` : ''}`;
    if (window.location.pathname + window.location.search !== target) {
      window.history.replaceState({}, '', target);
    }
  }, [sort, q]);

  const load = useCallback(async () => {
    setErr(null);
    const params = new URLSearchParams({ sort });
    if (q.trim()) params.set('q', q.trim());
    try {
      const r = await fetch(`/api/community/teams?${params.toString()}`);
      if (!r.ok) throw new Error('fetch failed');
      const j = (await r.json()) as ListResponse;
      setTeams(j.teams);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
      setTeams([]);
    }
  }, [sort, q]);

  // Debounce the search field so we don't fetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => void load(), 200);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div
      style={{
        maxWidth: 920,
        margin: '0 auto',
        padding: '32px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span
          className="caps mono"
          style={{ fontSize: 10, color: 'var(--fg-4)', letterSpacing: '0.1em' }}
        >
          community
        </span>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 28,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg-1)',
          }}
        >
          Teams from the community
        </h1>
        <p style={{ color: 'var(--fg-3)', margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
          Players post their rental teams here. Other players try them in
          Champions and mark whether the replica code works. The badge
          shows the running count — it's not verified by us.
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          paddingBottom: 12,
          borderBottom: '1px solid var(--line-1)',
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          {(['new', 'confirmed'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSort(s)}
              className="caps mono"
              style={{
                padding: '6px 12px',
                fontSize: 10,
                color: sort === s ? 'var(--accent-2)' : 'var(--fg-3)',
                background: sort === s ? 'var(--accent-bg)' : 'transparent',
                border: `1px solid ${sort === s ? 'var(--accent-line)' : 'var(--line-1)'}`,
                borderRadius: 3,
                letterSpacing: '0.08em',
                cursor: 'pointer',
              }}
            >
              {s === 'new' ? 'newest' : 'most confirmed'}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="search teams by name or blurb"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            padding: '6px 10px',
            background: 'var(--bg-1)',
            border: '1px solid var(--line-1)',
            borderRadius: 3,
            color: 'var(--fg-1)',
            fontSize: 13,
          }}
        />
      </div>

      {err && (
        <div style={{ color: 'var(--neg)', fontSize: 13 }}>Could not load teams. {err}</div>
      )}

      {teams === null && !err && (
        <div className="caps mono" style={{ color: 'var(--fg-4)', fontSize: 10 }}>
          loading…
        </div>
      )}

      {teams?.length === 0 && !err && (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--fg-3)',
            border: '1px dashed var(--line-1)',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          {q
            ? 'No teams match this search.'
            : 'No public teams yet. Be the first — publish from a team card.'}
        </div>
      )}

      {teams && teams.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {teams.map((t) => (
            <TeamRow key={t.id} team={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function TeamRow({ team }: { team: ListedTeam }) {
  const species = team.sprites;
  return (
    <button
      type="button"
      onClick={() => navTo(`/community/${team.id}`)}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 14,
        padding: 14,
        background: 'var(--bg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 4,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 4,
          alignItems: 'center',
          padding: 6,
          background: 'var(--bg-2)',
          borderRadius: 3,
          minWidth: 200,
        }}
      >
        {species.length === 0 ? (
          <div
            className="caps mono"
            style={{ color: 'var(--fg-4)', fontSize: 9.5, padding: '0 4px' }}
          >
            …
          </div>
        ) : (
          species.map((s, i) => <Sprite key={`${s}-${i}`} species={s} size={28} />)
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--fg-1)', fontWeight: 600, fontSize: 14 }}>{team.name}</span>
          {team.isOwn && (
            <span
              className="caps mono"
              style={{
                fontSize: 9,
                color: 'var(--accent-2)',
                background: 'var(--accent-bg)',
                padding: '1px 5px',
                borderRadius: 2,
                letterSpacing: '0.08em',
              }}
            >
              yours
            </span>
          )}
        </div>
        {team.blurb && (
          <span
            style={{
              color: 'var(--fg-3)',
              fontSize: 12.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {team.blurb}
          </span>
        )}
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            fontSize: 11,
          }}
        >
          <span style={{ color: 'var(--fg-4)' }}>
            {team.authorDisplayName ?? 'anonymous'}
          </span>
          <span style={{ color: 'var(--fg-4)' }}>·</span>
          <span style={{ color: 'var(--fg-4)' }}>{formatRelativeTime(team.createdAt)}</span>
          {team.replicaCode && (
            <>
              <span style={{ color: 'var(--fg-4)' }}>·</span>
              <span
                className="mono"
                style={{ color: 'var(--fg-3)', fontSize: 11, letterSpacing: '0.04em' }}
              >
                {team.replicaCode}
              </span>
            </>
          )}
          {team.archetypeTags?.map((tag) => (
            <span
              key={tag}
              className="caps mono"
              style={{
                fontSize: 9,
                color: 'var(--fg-3)',
                padding: '1px 5px',
                border: '1px solid var(--line-1)',
                borderRadius: 2,
                letterSpacing: '0.08em',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <AttestationBadge works={team.worksCount} broken={team.brokenCount} compact />
    </button>
  );
}

// Shared by list rows and detail view. `compact` collapses to a vertical
// chip; the detail page uses the expanded form alongside the works/broken
// action buttons.
//
// Plain-language counts — "X tried · Y worked" reads at a glance with no
// vocabulary tax. The internal column is named `worksCount`/`brokenCount`
// in the API, but no part of that vocabulary leaks to the UI.
export function AttestationBadge({
  works,
  broken,
  compact,
}: {
  works: number;
  broken: number;
  compact?: boolean;
}) {
  const total = works + broken;
  // Tint thresholds — calm-on-low-signal: 2-vote lead before turning green,
  // 3 broken before amber.
  const tone: 'pos' | 'warn' | 'neutral' =
    works > broken + 2 ? 'pos' : broken >= 3 ? 'warn' : 'neutral';
  const color = tone === 'pos' ? 'var(--pos)' : tone === 'warn' ? 'var(--warn)' : 'var(--fg-3)';
  const bg =
    tone === 'pos'
      ? 'var(--pos-bg, rgba(120, 180, 120, 0.08))'
      : tone === 'warn'
        ? 'var(--warn-bg, rgba(220, 170, 80, 0.08))'
        : 'transparent';
  const border = tone === 'neutral' ? 'var(--line-1)' : color;
  const headline = total === 0 ? 'untested' : `${works}/${total} say it works`;
  const sub = total === 0 ? 'be the first to try it' : broken > 0 ? `${broken} reported issues` : 'no issues yet';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: compact ? 'column' : 'row',
        alignItems: 'center',
        gap: compact ? 2 : 10,
        padding: compact ? '6px 8px' : '8px 14px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 3,
        minWidth: compact ? 96 : undefined,
      }}
    >
      <span className="mono" style={{ color, fontSize: compact ? 12 : 13, fontWeight: 600 }}>
        {headline}
      </span>
      <span
        className="mono"
        style={{ fontSize: compact ? 9.5 : 10.5, color: 'var(--fg-4)' }}
      >
        {sub}
      </span>
    </div>
  );
}
